import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdStar, MdStarBorder, MdStarHalf } from "react-icons/md";

// One word per grade, shown next to the stars so nobody has to guess what "3" means.
const GRADE_LABELS = ["Insuffisant", "À améliorer", "Correct", "Bien", "Excellent"];

// Whole stars only. Older debriefs may hold half grades (3.5): they still
// display, and the next click replaces them with a whole grade.
const StarRating = ({
  value = 0,
  onChange,
  max = 5,
  readOnly = false,
  size = 28,
  showLabel = true,
}) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(0);

  const displayValue = hovered || value;
  const labelIndex = Math.ceil(displayValue) - 1;
  const label = showLabel && labelIndex >= 0 ? GRADE_LABELS[labelIndex] : null;

  const select = (grade) => {
    setHovered(0);
    onChange?.(grade);
  };

  return (
    <div className="flex items-center gap-3">
      <div
        className="flex items-center"
        role="group"
        aria-label={t("Note")}
        onMouseLeave={() => !readOnly && setHovered(0)}
      >
        {Array.from({ length: max }, (_, i) => {
          const grade = i + 1;
          const isFull = displayValue >= grade;
          const isHalf = !isFull && displayValue >= grade - 0.5;
          const Icon = isFull ? MdStar : isHalf ? MdStarHalf : MdStarBorder;

          return (
            <button
              key={grade}
              type="button"
              aria-pressed={Math.ceil(value) === grade}
              aria-label={`${grade}/${max}`}
              disabled={readOnly}
              className={`p-1 border-0 bg-transparent rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                readOnly ? "cursor-default" : "cursor-pointer"
              }`}
              onClick={() => select(grade)}
              onMouseEnter={() => !readOnly && setHovered(grade)}
              onFocus={() => !readOnly && setHovered(grade)}
              onBlur={() => setHovered(0)}
            >
              <Icon
                size={size}
                className={isFull || isHalf ? "text-yellow-400" : "text-gray-300"}
              />
            </button>
          );
        })}
      </div>
      {showLabel && (
        <span className="w-24 text-sm text-gray-600 whitespace-nowrap">
          {label ? t(label) : readOnly ? "" : t("Pas encore noté")}
        </span>
      )}
      {!readOnly && value > 0 && (
        <button
          type="button"
          className="text-xs text-gray-400 hover:text-gray-600 underline cursor-pointer"
          onClick={() => select(0)}
        >
          {t("Effacer")}
        </button>
      )}
    </div>
  );
};

export default StarRating;
